import React, { useMemo, useState, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { CompletedBatch, CompletedProduct, InventoryItem, Lot } from '../types/todo';
import {
  deleteCompletedBatchesByFilling,
  deleteCompletedProductsByName,
  clearAllInventory,
  addSupply,
  updateSupply,
  deleteSupply
} from '../store/slices/todoSlice';
import {
  createRawIngredientAsync,
  updateRawIngredientAsync,
  deleteRawIngredientAsync
} from '../store/slices/rawIngredientsSlice';
import { selectGroupedRawIngredients } from '../store/selectors/rawIngredientsSelectors';
import { AddFillingInventoryModal } from '../components/AddFillingInventoryModal';
import { AddProductInventoryModal } from '../components/AddProductInventoryModal';
import { LotDropdown } from '../components/LotDropdown';
import { RawIngredientsTable } from '../components/inventory/RawIngredientsTable';
import { calculateReservedInventory } from '../utils/inventoryUtils';
import { rawIngredientApiService } from '../services/rawIngredientApiService';

interface FillingTotal {
  fillingName: string;
  totalBatches: number;
  completionDates: string[];
  lastCompleted: string;
}

interface ProductTotal {
  productName: string;
  totalUnits: number;
  reservedUnits: number;
  availableUnits: number;
  completionDates: string[];
  lastCompleted: string;
}

type ExpandedSection = 'fillings' | 'products' | 'rawIngredients' | 'supplies' | null;

export const Inventory: React.FC = () => {
  const dispatch = useAppDispatch();
  const completedBatches = useAppSelector(state => state.todo.completedBatches);
  const completedProducts = useAppSelector(state => state.todo.completedProducts);
  const { ingredients: rawIngredients, loading: rawIngredientsLoading, error: rawIngredientsError } = useAppSelector(state => state.rawIngredients);
  const groupedRawIngredients = useAppSelector(selectGroupedRawIngredients);
  const supplies = useAppSelector(state => state.todo.supplies);
  const orders = useAppSelector(state => state.orders.orders);
  const [error, setError] = useState<string | null>(null);

  // Check if localStorage migration is needed
  useEffect(() => {
    const stored = localStorage.getItem('raw-ingredients');
    if (stored) {
      try {
        const data = JSON.parse(stored);
        if (Array.isArray(data) && data.length > 0) {
          setHasLocalStorageData(true);
        }
      } catch (error) {
        console.error('Error checking localStorage:', error);
      }
    }
  }, []);
  const [showFillingsModal, setShowFillingsModal] = useState(false);
  const [showProductsModal, setShowProductsModal] = useState(false);
  const [expandedSection, setExpandedSection] = useState<ExpandedSection>(null);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editingItemName, setEditingItemName] = useState('');
  const [editingItemLotNumber, setEditingItemLotNumber] = useState('');
  const [editingItemUnitSize, setEditingItemUnitSize] = useState('');
  const [editingItemQuantity, setEditingItemQuantity] = useState(0);
  const [editingItemLots, setEditingItemLots] = useState<Lot[]>([]);
  const [hasLocalStorageData, setHasLocalStorageData] = useState(false);
  const [isMigrating, setIsMigrating] = useState(false);

  // Calculate totals by filling type
  const fillingTotals: FillingTotal[] = useMemo(() => {
    const totalsMap: { [fillingName: string]: FillingTotal } = {};

    completedBatches.forEach((batch: CompletedBatch) => {
      if (!totalsMap[batch.fillingName]) {
        totalsMap[batch.fillingName] = {
          fillingName: batch.fillingName,
          totalBatches: 0,
          completionDates: [],
          lastCompleted: batch.completedAt
        };
      }

      totalsMap[batch.fillingName].totalBatches += batch.batchCount;
      totalsMap[batch.fillingName].completionDates.push(batch.completedAt);

      // Update last completed if this is more recent
      if (new Date(batch.completedAt) > new Date(totalsMap[batch.fillingName].lastCompleted)) {
        totalsMap[batch.fillingName].lastCompleted = batch.completedAt;
      }
    });

    // Convert to array and sort by filling name
    return Object.values(totalsMap).sort((a, b) => a.fillingName.localeCompare(b.fillingName));
  }, [completedBatches]);

  // Calculate totals by product type
  const productTotals: ProductTotal[] = useMemo(() => {
    const totalsMap: { [productName: string]: ProductTotal } = {};

    completedProducts.forEach((product: CompletedProduct) => {
      if (!totalsMap[product.productName]) {
        totalsMap[product.productName] = {
          productName: product.productName,
          totalUnits: 0,
          reservedUnits: 0,
          availableUnits: 0,
          completionDates: [],
          lastCompleted: product.completedAt
        };
      }

      totalsMap[product.productName].totalUnits += product.unitCount;
      totalsMap[product.productName].completionDates.push(product.completedAt);

      // Update last completed if this is more recent
      if (new Date(product.completedAt) > new Date(totalsMap[product.productName].lastCompleted)) {
        totalsMap[product.productName].lastCompleted = product.completedAt;
      }
    });

    // Calculate reserved quantities from orders
    const reservedInventory = calculateReservedInventory(orders);

    // Update each product with reserved and available quantities
    Object.values(totalsMap).forEach(product => {
      product.reservedUnits = reservedInventory.get(product.productName) || 0;
      product.availableUnits = Math.max(0, product.totalUnits - product.reservedUnits);
    });

    // Convert to array and sort by product name
    return Object.values(totalsMap).sort((a, b) => a.productName.localeCompare(b.productName));
  }, [completedProducts, orders]);

  const formatDate = (isoString: string): string => {
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const toggleSection = (section: ExpandedSection) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const handleDeleteFilling = (fillingName: string, totalBatches: number) => {
    const confirmMessage = `Are you sure you want to delete all ${totalBatches} batch${totalBatches !== 1 ? 'es' : ''} of ${fillingName}? This action cannot be undone.`;

    if (window.confirm(confirmMessage)) {
      try {
        dispatch(deleteCompletedBatchesByFilling(fillingName));
        setError(null);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to delete batches';
        setError(errorMessage);
        console.error('Error deleting batches:', err);
      }
    }
  };

  const handleDeleteProduct = (productName: string, totalUnits: number) => {
    const confirmMessage = `Are you sure you want to delete all ${totalUnits} unit${totalUnits !== 1 ? 's' : ''} of ${productName}? This action cannot be undone.`;

    if (window.confirm(confirmMessage)) {
      try {
        dispatch(deleteCompletedProductsByName(productName));
        setError(null);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to delete products';
        setError(errorMessage);
        console.error('Error deleting products:', err);
      }
    }
  };

  const handleDeleteAllInventory = () => {
    const confirmMessage = 'Are you sure you want to delete all inventory? This cannot be undone.';

    if (window.confirm(confirmMessage)) {
      try {
        dispatch(clearAllInventory());
        setError(null);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to delete all inventory';
        setError(errorMessage);
        console.error('Error deleting all inventory:', err);
      }
    }
  };

  // Raw Ingredients handlers
  const handleAddRawIngredient = () => {
    alert('Raw ingredients can only be created from the Ingredient Receiving page. They are automatically added to inventory when you create a receiving log entry.');
  };

  const handleStartEditingItem = (item: InventoryItem) => {
    setEditingItemId(item.id);
    setEditingItemName(item.name);
    setEditingItemLotNumber(item.lotNumber || '');
    setEditingItemUnitSize(item.unitSize || '');
    setEditingItemQuantity(item.quantity);
    setEditingItemLots(item.lots || []);
  };

  const handleSaveRawIngredient = (item: InventoryItem) => {
    if (editingItemName.trim()) {
      // Calculate total quantity from lots
      const totalQuantity =
        editingItemLots.length > 0 ? editingItemLots.reduce((sum, lot) => sum + lot.quantity, 0) : editingItemQuantity;

      dispatch(
        updateRawIngredientAsync({
          ...item,
          name: editingItemName,
          lotNumber: editingItemLotNumber,
          unitSize: editingItemUnitSize,
          quantity: totalQuantity,
          lots: editingItemLots.length > 0 ? editingItemLots : undefined,
          receivingLogId: item.receivingLogId || '',
          createdAt: item.createdAt,
          updatedAt: new Date().toISOString()
        })
      );
      setEditingItemId(null);
    }
  };

  const handleCancelEdit = () => {
    setEditingItemId(null);
  };

  const handleDeleteRawIngredient = (id: string, name: string) => {
    const confirmMessage = `Are you sure you want to delete "${name}"?`;
    if (window.confirm(confirmMessage)) {
      dispatch(deleteRawIngredientAsync(id));
    }
  };

  const handleMigrateFromLocalStorage = async () => {
    const confirmMessage =
      'This will migrate your raw ingredients from browser storage to the database. ' +
      'This is a one-time operation. Continue?';

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setIsMigrating(true);

    try {
      const stored = localStorage.getItem('raw-ingredients');
      if (!stored) {
        alert('No data found in local storage.');
        setIsMigrating(false);
        return;
      }

      const legacyData = JSON.parse(stored);
      if (!Array.isArray(legacyData) || legacyData.length === 0) {
        alert('No data to migrate.');
        setIsMigrating(false);
        return;
      }

      // Note: Since raw ingredients require receivingLogId (NOT NULL),
      // we cannot import legacy localStorage data directly.
      // This migration is only for informational purposes.
      alert(
        `Found ${legacyData.length} raw ingredients in local storage.\n\n` +
        'However, raw ingredients must be linked to a receiving log entry. ' +
        'Please create receiving log entries in the Ingredient Receiving page, ' +
        'which will automatically create raw ingredients in the inventory.\n\n' +
        'Your local storage data will not be migrated automatically.'
      );

      setIsMigrating(false);
      setHasLocalStorageData(false);
    } catch (error) {
      console.error('Error during migration:', error);
      alert(`Migration failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setIsMigrating(false);
    }
  };

  // Supplies handlers
  const handleAddSupply = () => {
    dispatch(addSupply({ name: '', quantity: 0 }));
  };

  const handleSaveSupply = (item: InventoryItem) => {
    if (editingItemName.trim()) {
      // Calculate total quantity from lots
      const totalQuantity =
        editingItemLots.length > 0 ? editingItemLots.reduce((sum, lot) => sum + lot.quantity, 0) : editingItemQuantity;

      dispatch(
        updateSupply({
          ...item,
          name: editingItemName,
          lotNumber: editingItemLotNumber,
          unitSize: editingItemUnitSize,
          quantity: totalQuantity,
          lots: editingItemLots.length > 0 ? editingItemLots : undefined
        })
      );
      setEditingItemId(null);
    }
  };

  const handleDeleteSupply = (id: string, name: string) => {
    const confirmMessage = `Are you sure you want to delete "${name}"?`;
    if (window.confirm(confirmMessage)) {
      dispatch(deleteSupply(id));
    }
  };

  return (
    <div className="container" style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <header
        style={{ marginBottom: '40px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
      >
        <div>
          <h1 style={{ fontSize: '2.5rem', color: '#333' }}>Inventory Management</h1>
          <p style={{ color: '#666', fontSize: '1.1rem', marginTop: '8px' }}>
            Track and manage your ingredient inventory
          </p>
        </div>
        <button
          onClick={handleDeleteAllInventory}
          style={{
            backgroundColor: '#f44336',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            padding: '12px 24px',
            fontSize: '14px',
            cursor: 'pointer',
            fontWeight: '600',
            transition: 'background-color 0.2s',
            boxShadow: '0 2px 4px rgba(244, 67, 54, 0.3)',
            marginTop: '8px'
          }}
          onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#d32f2f')}
          onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#f44336')}
          title="Delete all inventory data"
        >
          Delete All Inventory
        </button>
      </header>

      <section style={{ marginBottom: '30px' }}>
        <div
          style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            overflow: 'hidden'
          }}
        >
          <div
            onClick={() => toggleSection('fillings')}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '20px',
              cursor: 'pointer',
              backgroundColor: '#f8f9fa',
              borderBottom: expandedSection === 'fillings' ? '2px solid #e0e0e0' : 'none',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#e9ecef')}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#f8f9fa')}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <h2 style={{ fontSize: '1.8rem', color: '#333', margin: 0 }}>Fillings</h2>
              <span
                style={{
                  fontSize: '1.2rem',
                  color: '#666',
                  transition: 'transform 0.3s',
                  transform: expandedSection === 'fillings' ? 'rotate(180deg)' : 'rotate(0deg)',
                  display: 'inline-block'
                }}
              >
                ▼
              </span>
            </div>
            {expandedSection === 'fillings' && (
              <button
                onClick={e => {
                  e.stopPropagation();
                  setShowFillingsModal(true);
                }}
                style={{
                  backgroundColor: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '10px 20px',
                  fontSize: '14px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  transition: 'background-color 0.2s',
                  boxShadow: '0 2px 4px rgba(76, 175, 80, 0.3)'
                }}
                onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#45a049')}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#4CAF50')}
              >
                Add Inventory
              </button>
            )}
          </div>

          {expandedSection === 'fillings' && (
            <>
              {error && (
                <div
                  style={{
                    padding: '12px 16px',
                    backgroundColor: '#fee',
                    border: '1px solid #fcc',
                    borderRadius: '4px',
                    color: '#c33',
                    margin: '16px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <span>{error}</span>
                  <button
                    onClick={() => setError(null)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#c33',
                      cursor: 'pointer',
                      fontSize: '18px',
                      padding: '0 8px'
                    }}
                  >
                    ×
                  </button>
                </div>
              )}

              {fillingTotals.length > 0 ? (
                <>
                  <table
                    style={{
                      width: '100%',
                      borderCollapse: 'collapse'
                    }}
                  >
                    <thead>
                      <tr
                        style={{
                          backgroundColor: '#f8f9fa',
                          borderBottom: '2px solid #e0e0e0'
                        }}
                      >
                        <th
                          style={{
                            padding: '16px',
                            textAlign: 'left',
                            fontWeight: '600',
                            color: '#555',
                            fontSize: '14px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}
                        >
                          Filling Type
                        </th>
                        <th
                          style={{
                            padding: '16px',
                            textAlign: 'center',
                            fontWeight: '600',
                            color: '#555',
                            fontSize: '14px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}
                        >
                          Total Batches
                        </th>
                        <th
                          style={{
                            padding: '16px',
                            textAlign: 'left',
                            fontWeight: '600',
                            color: '#555',
                            fontSize: '14px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}
                        >
                          Last Completed
                        </th>
                        <th
                          style={{
                            padding: '16px',
                            textAlign: 'center',
                            fontWeight: '600',
                            color: '#555',
                            fontSize: '14px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}
                        >
                          Completions
                        </th>
                        <th
                          style={{
                            padding: '16px',
                            textAlign: 'center',
                            fontWeight: '600',
                            color: '#555',
                            fontSize: '14px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            width: '100px'
                          }}
                        >
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {fillingTotals.map((filling, index) => (
                        <tr
                          key={filling.fillingName}
                          style={{
                            borderBottom: index < fillingTotals.length - 1 ? '1px solid #f0f0f0' : 'none',
                            transition: 'background-color 0.2s'
                          }}
                          onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f8f9fa')}
                          onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'white')}
                        >
                          <td
                            style={{
                              padding: '16px',
                              fontWeight: '600',
                              color: '#333',
                              fontSize: '15px'
                            }}
                          >
                            {filling.fillingName}
                          </td>
                          <td
                            style={{
                              padding: '16px',
                              textAlign: 'center',
                              fontSize: '18px',
                              fontWeight: '700',
                              color: '#4CAF50'
                            }}
                          >
                            {filling.totalBatches}
                          </td>
                          <td
                            style={{
                              padding: '16px',
                              fontSize: '14px',
                              color: '#666'
                            }}
                          >
                            {formatDate(filling.lastCompleted)}
                          </td>
                          <td
                            style={{
                              padding: '16px',
                              textAlign: 'center',
                              fontSize: '14px',
                              color: '#666'
                            }}
                          >
                            {filling.completionDates.length}
                          </td>
                          <td
                            style={{
                              padding: '16px',
                              textAlign: 'center'
                            }}
                          >
                            <button
                              onClick={() => handleDeleteFilling(filling.fillingName, filling.totalBatches)}
                              style={{
                                backgroundColor: '#f44336',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '6px 12px',
                                fontSize: '13px',
                                cursor: 'pointer',
                                fontWeight: '500',
                                transition: 'background-color 0.2s'
                              }}
                              onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#d32f2f')}
                              onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#f44336')}
                              title={`Delete all ${filling.totalBatches} batch${filling.totalBatches !== 1 ? 'es' : ''} of ${filling.fillingName}`}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <div
                    style={{
                      padding: '16px',
                      backgroundColor: '#f8f9fa',
                      borderTop: '2px solid #e0e0e0',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <span
                      style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#555'
                      }}
                    >
                      Total Batches Completed:
                    </span>
                    <span
                      style={{
                        fontSize: '20px',
                        fontWeight: '700',
                        color: '#4CAF50'
                      }}
                    >
                      {fillingTotals.reduce((sum, f) => sum + f.totalBatches, 0)}
                    </span>
                  </div>
                </>
              ) : (
                <div
                  style={{
                    padding: '40px',
                    backgroundColor: '#f5f5f5',
                    borderRadius: '8px',
                    textAlign: 'center',
                    margin: '16px'
                  }}
                >
                  <p style={{ color: '#666', fontSize: '1.1rem' }}>
                    No completed batches yet. Complete tasks on the schedule page to see totals here.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <section style={{ marginBottom: '30px' }}>
        <div
          style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            overflow: 'hidden'
          }}
        >
          <div
            onClick={() => toggleSection('products')}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '20px',
              cursor: 'pointer',
              backgroundColor: '#f8f9fa',
              borderBottom: expandedSection === 'products' ? '2px solid #e0e0e0' : 'none',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#e9ecef')}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#f8f9fa')}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <h2 style={{ fontSize: '1.8rem', color: '#333', margin: 0 }}>Finished Products</h2>
              <span
                style={{
                  fontSize: '1.2rem',
                  color: '#666',
                  transition: 'transform 0.3s',
                  transform: expandedSection === 'products' ? 'rotate(180deg)' : 'rotate(0deg)',
                  display: 'inline-block'
                }}
              >
                ▼
              </span>
            </div>
            {expandedSection === 'products' && (
              <button
                onClick={e => {
                  e.stopPropagation();
                  setShowProductsModal(true);
                }}
                style={{
                  backgroundColor: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '10px 20px',
                  fontSize: '14px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  transition: 'background-color 0.2s',
                  boxShadow: '0 2px 4px rgba(76, 175, 80, 0.3)'
                }}
                onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#45a049')}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#4CAF50')}
              >
                Add Inventory
              </button>
            )}
          </div>

          {expandedSection === 'products' && (
            <>
              {productTotals.length > 0 ? (
                <>
                  <table
                    style={{
                      width: '100%',
                      borderCollapse: 'collapse'
                    }}
                  >
                    <thead>
                      <tr
                        style={{
                          backgroundColor: '#f8f9fa',
                          borderBottom: '2px solid #e0e0e0'
                        }}
                      >
                        <th
                          style={{
                            padding: '16px',
                            textAlign: 'left',
                            fontWeight: '600',
                            color: '#555',
                            fontSize: '14px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}
                        >
                          Product Name
                        </th>
                        <th
                          style={{
                            padding: '16px',
                            textAlign: 'center',
                            fontWeight: '600',
                            color: '#555',
                            fontSize: '14px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}
                        >
                          Total Units (Reserved/Available)
                        </th>
                        <th
                          style={{
                            padding: '16px',
                            textAlign: 'left',
                            fontWeight: '600',
                            color: '#555',
                            fontSize: '14px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}
                        >
                          Last Completed
                        </th>
                        <th
                          style={{
                            padding: '16px',
                            textAlign: 'center',
                            fontWeight: '600',
                            color: '#555',
                            fontSize: '14px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}
                        >
                          Completions
                        </th>
                        <th
                          style={{
                            padding: '16px',
                            textAlign: 'center',
                            fontWeight: '600',
                            color: '#555',
                            fontSize: '14px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            width: '100px'
                          }}
                        >
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {productTotals.map((product, index) => (
                        <tr
                          key={product.productName}
                          style={{
                            borderBottom: index < productTotals.length - 1 ? '1px solid #f0f0f0' : 'none',
                            transition: 'background-color 0.2s'
                          }}
                          onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f8f9fa')}
                          onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'white')}
                        >
                          <td
                            style={{
                              padding: '16px',
                              fontWeight: '600',
                              color: '#333',
                              fontSize: '15px'
                            }}
                          >
                            {product.productName}
                          </td>
                          <td
                            style={{
                              padding: '16px',
                              textAlign: 'center',
                              fontSize: '16px',
                              color: '#333'
                            }}
                          >
                            <div style={{ fontWeight: '700', fontSize: '18px', color: '#2196F3', marginBottom: '4px' }}>
                              {product.totalUnits}
                            </div>
                            <div style={{ fontSize: '13px', color: '#666', lineHeight: '1.4' }}>
                              <span style={{ color: '#FF9800', fontWeight: '600' }}>
                                {product.reservedUnits} reserved
                              </span>
                              {' • '}
                              <span style={{ color: '#4CAF50', fontWeight: '600' }}>
                                {product.availableUnits} available
                              </span>
                            </div>
                          </td>
                          <td
                            style={{
                              padding: '16px',
                              fontSize: '14px',
                              color: '#666'
                            }}
                          >
                            {formatDate(product.lastCompleted)}
                          </td>
                          <td
                            style={{
                              padding: '16px',
                              textAlign: 'center',
                              fontSize: '14px',
                              color: '#666'
                            }}
                          >
                            {product.completionDates.length}
                          </td>
                          <td
                            style={{
                              padding: '16px',
                              textAlign: 'center'
                            }}
                          >
                            <button
                              onClick={() => handleDeleteProduct(product.productName, product.totalUnits)}
                              style={{
                                backgroundColor: '#f44336',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '6px 12px',
                                fontSize: '13px',
                                cursor: 'pointer',
                                fontWeight: '500',
                                transition: 'background-color 0.2s'
                              }}
                              onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#d32f2f')}
                              onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#f44336')}
                              title={`Delete all ${product.totalUnits} unit${product.totalUnits !== 1 ? 's' : ''} of ${product.productName}`}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <div
                    style={{
                      padding: '16px',
                      backgroundColor: '#f8f9fa',
                      borderTop: '2px solid #e0e0e0',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#555', marginBottom: '4px' }}>
                        Total Units Completed:
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        <span style={{ color: '#FF9800', fontWeight: '600' }}>
                          {productTotals.reduce((sum, p) => sum + p.reservedUnits, 0)} reserved
                        </span>
                        {' • '}
                        <span style={{ color: '#4CAF50', fontWeight: '600' }}>
                          {productTotals.reduce((sum, p) => sum + p.availableUnits, 0)} available
                        </span>
                      </div>
                    </div>
                    <span
                      style={{
                        fontSize: '20px',
                        fontWeight: '700',
                        color: '#2196F3'
                      }}
                    >
                      {productTotals.reduce((sum, p) => sum + p.totalUnits, 0)}
                    </span>
                  </div>
                </>
              ) : (
                <div
                  style={{
                    padding: '40px',
                    backgroundColor: '#f5f5f5',
                    borderRadius: '8px',
                    textAlign: 'center',
                    margin: '16px'
                  }}
                >
                  <p style={{ color: '#666', fontSize: '1.1rem' }}>
                    No completed products yet. Complete order fulfillment tasks on the schedule page to see totals here.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <section style={{ marginBottom: '30px' }}>
        <div
          style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            overflow: 'visible'
          }}
        >
          <div
            onClick={() => toggleSection('rawIngredients')}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '20px',
              cursor: 'pointer',
              backgroundColor: '#f8f9fa',
              borderBottom: expandedSection === 'rawIngredients' ? '2px solid #e0e0e0' : 'none',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#e9ecef')}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#f8f9fa')}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <h2 style={{ fontSize: '1.8rem', color: '#333', margin: 0 }}>Raw Ingredients</h2>
              <span
                style={{
                  fontSize: '1.2rem',
                  color: '#666',
                  transition: 'transform 0.3s',
                  transform: expandedSection === 'rawIngredients' ? 'rotate(180deg)' : 'rotate(0deg)',
                  display: 'inline-block'
                }}
              >
                ▼
              </span>
            </div>
            {expandedSection === 'rawIngredients' && (
              <div style={{ display: 'flex', gap: '10px' }}>
                {hasLocalStorageData && (
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      handleMigrateFromLocalStorage();
                    }}
                    disabled={isMigrating}
                    style={{
                      backgroundColor: isMigrating ? '#ccc' : '#FF9800',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '10px 20px',
                      fontSize: '14px',
                      cursor: isMigrating ? 'not-allowed' : 'pointer',
                      fontWeight: '600',
                      transition: 'background-color 0.2s',
                      boxShadow: '0 2px 4px rgba(255, 152, 0, 0.3)'
                    }}
                    onMouseEnter={e => !isMigrating && (e.currentTarget.style.backgroundColor = '#F57C00')}
                    onMouseLeave={e => !isMigrating && (e.currentTarget.style.backgroundColor = '#FF9800')}
                  >
                    {isMigrating ? 'Checking...' : 'Migrate from Local Storage'}
                  </button>
                )}
                <button
                  onClick={e => {
                    e.stopPropagation();
                    handleAddRawIngredient();
                  }}
                  style={{
                    backgroundColor: '#4CAF50',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '10px 20px',
                    fontSize: '14px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    transition: 'background-color 0.2s',
                    boxShadow: '0 2px 4px rgba(76, 175, 80, 0.3)'
                  }}
                  onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#45a049')}
                  onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#4CAF50')}
                >
                  Add Item
                </button>
              </div>
            )}
          </div>

          {expandedSection === 'rawIngredients' && (
            <RawIngredientsTable
              ingredients={groupedRawIngredients}
              loading={rawIngredientsLoading}
              error={rawIngredientsError}
            />
          )}
        </div>
      </section>

      <section style={{ marginBottom: '30px' }}>
        <div
          style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            overflow: 'visible'
          }}
        >
          <div
            onClick={() => toggleSection('supplies')}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '20px',
              cursor: 'pointer',
              backgroundColor: '#f8f9fa',
              borderBottom: expandedSection === 'supplies' ? '2px solid #e0e0e0' : 'none',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#e9ecef')}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#f8f9fa')}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <h2 style={{ fontSize: '1.8rem', color: '#333', margin: 0 }}>Supplies</h2>
              <span
                style={{
                  fontSize: '1.2rem',
                  color: '#666',
                  transition: 'transform 0.3s',
                  transform: expandedSection === 'supplies' ? 'rotate(180deg)' : 'rotate(0deg)',
                  display: 'inline-block'
                }}
              >
                ▼
              </span>
            </div>
            {expandedSection === 'supplies' && (
              <button
                onClick={e => {
                  e.stopPropagation();
                  handleAddSupply();
                }}
                style={{
                  backgroundColor: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '10px 20px',
                  fontSize: '14px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  transition: 'background-color 0.2s',
                  boxShadow: '0 2px 4px rgba(76, 175, 80, 0.3)'
                }}
                onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#45a049')}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#4CAF50')}
              >
                Add Item
              </button>
            )}
          </div>

          {expandedSection === 'supplies' && (
            <>
              {supplies.length > 0 ? (
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse'
                  }}
                >
                  <thead>
                    <tr
                      style={{
                        backgroundColor: '#f8f9fa',
                        borderBottom: '2px solid #e0e0e0'
                      }}
                    >
                      <th
                        style={{
                          padding: '16px',
                          textAlign: 'left',
                          fontWeight: '600',
                          color: '#555',
                          fontSize: '14px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}
                      >
                        Item Name
                      </th>
                      <th
                        style={{
                          padding: '16px',
                          textAlign: 'left',
                          fontWeight: '600',
                          color: '#555',
                          fontSize: '14px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          width: '300px'
                        }}
                      >
                        Lots
                      </th>
                      <th
                        style={{
                          padding: '16px',
                          textAlign: 'left',
                          fontWeight: '600',
                          color: '#555',
                          fontSize: '14px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          width: '180px'
                        }}
                      >
                        Unit Size
                      </th>
                      <th
                        style={{
                          padding: '16px',
                          textAlign: 'center',
                          fontWeight: '600',
                          color: '#555',
                          fontSize: '14px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          width: '150px'
                        }}
                      >
                        Quantity
                      </th>
                      <th
                        style={{
                          padding: '16px',
                          textAlign: 'center',
                          fontWeight: '600',
                          color: '#555',
                          fontSize: '14px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          width: '150px'
                        }}
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {supplies.map((item, index) => (
                      <tr
                        key={item.id}
                        style={{
                          borderBottom: index < supplies.length - 1 ? '1px solid #f0f0f0' : 'none',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f8f9fa')}
                        onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'white')}
                      >
                        <td style={{ padding: '16px' }}>
                          {editingItemId === item.id ? (
                            <input
                              type="text"
                              value={editingItemName}
                              onChange={e => setEditingItemName(e.target.value)}
                              style={{
                                width: '100%',
                                padding: '8px',
                                fontSize: '15px',
                                border: '2px solid #4CAF50',
                                borderRadius: '4px',
                                fontWeight: '600'
                              }}
                              placeholder="Item name"
                              autoFocus
                            />
                          ) : (
                            <span
                              style={{
                                fontWeight: '600',
                                color: '#333',
                                fontSize: '15px'
                              }}
                            >
                              {item.name || '(unnamed)'}
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '16px' }}>
                          <LotDropdown
                            lots={editingItemId === item.id ? editingItemLots : item.lots || []}
                            onChange={setEditingItemLots}
                            disabled={editingItemId !== item.id}
                          />
                        </td>
                        <td style={{ padding: '16px' }}>
                          {editingItemId === item.id ? (
                            <input
                              type="text"
                              value={editingItemUnitSize}
                              onChange={e => setEditingItemUnitSize(e.target.value)}
                              style={{
                                width: '100%',
                                padding: '8px',
                                fontSize: '15px',
                                border: '2px solid #4CAF50',
                                borderRadius: '4px',
                                fontWeight: '600'
                              }}
                              placeholder="e.g., 5 kg"
                            />
                          ) : (
                            <span
                              style={{
                                fontWeight: '500',
                                color: '#666',
                                fontSize: '14px'
                              }}
                            >
                              {item.unitSize || '-'}
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '16px', textAlign: 'center' }}>
                          <span
                            style={{
                              fontSize: '18px',
                              fontWeight: '700',
                              color: '#4CAF50'
                            }}
                          >
                            {editingItemId === item.id
                              ? editingItemLots.reduce((sum, lot) => sum + lot.quantity, 0)
                              : item.quantity}
                          </span>
                        </td>
                        <td style={{ padding: '16px', textAlign: 'center' }}>
                          {editingItemId === item.id ? (
                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                              <button
                                onClick={() => handleSaveSupply(item)}
                                style={{
                                  backgroundColor: '#4CAF50',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  padding: '6px 12px',
                                  fontSize: '13px',
                                  cursor: 'pointer',
                                  fontWeight: '500',
                                  transition: 'background-color 0.2s'
                                }}
                                onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#45a049')}
                                onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#4CAF50')}
                              >
                                Save
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                style={{
                                  backgroundColor: '#757575',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  padding: '6px 12px',
                                  fontSize: '13px',
                                  cursor: 'pointer',
                                  fontWeight: '500',
                                  transition: 'background-color 0.2s'
                                }}
                                onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#616161')}
                                onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#757575')}
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                              <button
                                onClick={() => handleStartEditingItem(item)}
                                style={{
                                  backgroundColor: '#2196F3',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  padding: '6px 12px',
                                  fontSize: '13px',
                                  cursor: 'pointer',
                                  fontWeight: '500',
                                  transition: 'background-color 0.2s'
                                }}
                                onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#1976D2')}
                                onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#2196F3')}
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteSupply(item.id, item.name)}
                                style={{
                                  backgroundColor: '#f44336',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  padding: '6px 12px',
                                  fontSize: '13px',
                                  cursor: 'pointer',
                                  fontWeight: '500',
                                  transition: 'background-color 0.2s'
                                }}
                                onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#d32f2f')}
                                onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#f44336')}
                              >
                                Delete
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div
                  style={{
                    padding: '40px',
                    backgroundColor: '#f5f5f5',
                    borderRadius: '8px',
                    textAlign: 'center',
                    margin: '16px'
                  }}
                >
                  <p style={{ color: '#666', fontSize: '1.1rem' }}>
                    No supplies added yet. Click "Add Item" to get started.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {showFillingsModal && <AddFillingInventoryModal onClose={() => setShowFillingsModal(false)} />}

      {showProductsModal && <AddProductInventoryModal onClose={() => setShowProductsModal(false)} />}
    </div>
  );
};
